import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { extractErrorMessage } from '../../utils/errorUtils';
import { formatMoney } from '../reports/formatters';
import { useGetOptionsQuery, useLazyGenerateReportQuery } from './api/revenueReportApi';
import { exportRevenueReport } from './exportRevenueReport';
import type { RevenueReportOption, RevenueReportRequest, RevenueReportRow } from './types';

const createRequest = (
  period: string,
  companyId: number | null,
  employeeIds: number[]
): RevenueReportRequest => ({
  Period: period,
  CompanyId: companyId,
  EmployeeIds: employeeIds,
});

const ReportRow = ({ row }: { row: RevenueReportRow }) => (
  <TableRow>
    <TableCell>{row.employeeName}</TableCell>
    <TableCell>{row.currencyName}</TableCell>
    <TableCell align="right">{formatMoney(row.summary)}</TableCell>
    <TableCell align="right">{formatMoney(row.cost)}</TableCell>
    <TableCell align="right">{formatMoney(row.revenue)}</TableCell>
    <TableCell align="right">{formatMoney(row.revenueEur)}</TableCell>
  </TableRow>
);

export const RevenueReportPage = () => {
  const {
    data: initialOptions,
    isLoading: initialOptionsLoading,
    error: initialOptionsError,
  } = useGetOptionsQuery({});
  const [period, setPeriod] = useState<string | null | undefined>(undefined);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [employeeIds, setEmployeeIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const initialized = useRef(false);

  const periods = initialOptions?.periods ?? [];
  const defaultPeriod = periods[0]?.value ?? null;
  const activePeriod = period === undefined ? defaultPeriod : period;
  const {
    data: scopedOptions,
    isLoading: scopedOptionsLoading,
    error: scopedOptionsError,
  } = useGetOptionsQuery(activePeriod ? { period: activePeriod, companyId } : {}, {
    skip: !activePeriod,
  });
  const [
    generateReport,
    { data: report, isFetching: reportLoading, error: reportRequestError, reset: resetReport },
  ] = useLazyGenerateReportQuery();

  const companies = scopedOptions?.companies ?? [];
  const employees = useMemo(() => scopedOptions?.employees ?? [], [scopedOptions?.employees]);
  const selectedPeriod = periods.find((item) => item.value === activePeriod) ?? null;
  const selectedCompany = companies.find((item) => item.id === companyId) ?? null;
  const selectedEmployees = useMemo(
    () => employees.filter((employee) => employeeIds.includes(employee.id)),
    [employees, employeeIds]
  );
  const isLoading = initialOptionsLoading || scopedOptionsLoading || reportLoading;

  useEffect(() => {
    if (initialized.current || !activePeriod || !scopedOptions) return;

    initialized.current = true;
    void generateReport(createRequest(activePeriod, null, []));
  }, [activePeriod, generateReport, scopedOptions]);

  const handleGenerate = () => {
    if (!activePeriod) {
      setError('Period is required.');
      return;
    }

    setError(null);
    void generateReport(createRequest(activePeriod, companyId, employeeIds));
  };

  const handlePeriodChange = (value: string | null) => {
    setPeriod(value);
    setCompanyId(null);
    setEmployeeIds([]);
    setError(null);
    resetReport();
  };

  const handleCompanyChange = (value: RevenueReportOption | null) => {
    setCompanyId(value?.id ?? null);
    setEmployeeIds([]);
    setError(null);
    resetReport();
  };

  const handleClear = () => {
    setPeriod(null);
    setCompanyId(null);
    setEmployeeIds([]);
    setError(null);
    resetReport();
  };

  const handleExport = async () => {
    if (!report || report.rows.length === 0) return;

    try {
      await exportRevenueReport(report);
    } catch (exportError) {
      setError(extractErrorMessage(exportError, 'Failed to export revenue report.'));
    }
  };

  return (
    <Box
      component="section"
      aria-label="Revenue Report"
      sx={{ p: { xs: 1, md: 2 }, height: '100%', overflow: 'auto' }}
    >
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <Autocomplete
            fullWidth
            options={periods}
            value={selectedPeriod}
            onChange={(_event, value) => handlePeriodChange(value?.value ?? null)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disabled={isLoading}
            renderInput={(params) => <TextField {...params} label="Period" />}
          />
          <Autocomplete
            fullWidth
            options={companies}
            value={selectedCompany}
            onChange={(_event, value) => handleCompanyChange(value)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={isLoading || !activePeriod}
            renderInput={(params) => (
              <TextField {...params} label="Company" placeholder="All companies" />
            )}
          />
          <Autocomplete<RevenueReportOption, true>
            multiple
            fullWidth
            options={employees}
            value={selectedEmployees}
            onChange={(_event, value) => setEmployeeIds(value.map((employee) => employee.id))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableCloseOnSelect
            disabled={isLoading || !activePeriod}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox checked={selected} sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Employee" placeholder="All employees" />
            )}
          />
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, height: 42 }}>
            <Button variant="contained" onClick={handleGenerate} disabled={isLoading}>
              {reportLoading ? 'Loading...' : 'Generate Report'}
            </Button>
            <Button variant="outlined" onClick={handleClear} disabled={isLoading}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {(initialOptionsError || scopedOptionsError || reportRequestError || error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ||
            extractErrorMessage(
              reportRequestError || scopedOptionsError || initialOptionsError,
              'Failed to load revenue report.'
            )}
        </Alert>
      )}
      {report?.warnings.map((warning) => (
        <Alert key={warning} severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      ))}
      {report && report.excludedLines.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            Excluded invoice lines
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {report.excludedLines.map((line) => {
              const invoice = line.invoiceNumber
                ? `Invoice ${line.invoiceNumber}`
                : `Invoice #${line.invoiceId}`;
              const description = line.description ? ` — ${line.description}` : '';
              const employee = line.employeeName ? ` Employee: ${line.employeeName}.` : '';

              return (
                <Typography component="li" variant="body2" key={line.invoiceLineId}>
                  {invoice}, line #{line.invoiceLineId}
                  {description}: {line.reason}
                  {employee}
                </Typography>
              );
            })}
          </Box>
        </Alert>
      )}
      {isLoading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {report && (
        <Paper variant="outlined">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 1.5 }}
          >
            <Typography variant="body2" color="text.secondary">
              {report.periodCaption}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => void handleExport()}
              disabled={report.rows.length === 0}
            >
              Export to Excel
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '23%' }}>Employee</TableCell>
                  <TableCell sx={{ width: '13%' }}>Invoice Currency</TableCell>
                  <TableCell sx={{ width: '16%' }} align="right">
                    Summary
                  </TableCell>
                  <TableCell sx={{ width: '16%' }} align="right">
                    Cost
                  </TableCell>
                  <TableCell sx={{ width: '16%' }} align="right">
                    Revenue
                  </TableCell>
                  <TableCell sx={{ width: '16%' }} align="right">
                    Revenue EUR
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.rows.length > 0 ? (
                  report.rows.map((row) => <ReportRow key={row.id} row={row} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {report.employeeTotals.length > 0 && (
            <Box
              sx={{
                borderTop: 2,
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
                mt: 1,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
                Total Revenue EUR by Employee
              </Typography>
              <Table size="small" sx={{ bgcolor: 'background.paper' }}>
                <TableBody>
                  {report.employeeTotals.map((total) => (
                    <TableRow key={total.employeeId}>
                      <TableCell>{total.employeeName}</TableCell>
                      <TableCell align="right">{formatMoney(total.totalRevenueEur)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{
                      backgroundColor: 'action.selected',
                      borderTop: 1,
                      borderColor: 'divider',
                      '& > *': { fontWeight: 700 },
                    }}
                  >
                    <TableCell>Grand Total Revenue EUR</TableCell>
                    <TableCell align="right">{formatMoney(report.grandTotalRevenueEur)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};
